import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { Save, Edit3, Eye, Trash2, Plus, RefreshCw, Globe, FileText, Settings, Search, AlertCircle, CheckCircle, Database, Users, Copy } from 'lucide-react';
import { frontendContentService } from '../../lib/supabase';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Select from '../../components/ui/Select';

const UniversalContentManagementSystem = () => {
  // State management for content
  const [contentItems, setContentItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showEditor, setShowEditor] = useState(false);
  const [selectedContent, setSelectedContent] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterPage, setFilterPage] = useState('all');
  const [filterType, setFilterType] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [contentStats, setContentStats] = useState(null);

  // Advanced features state
  const [showBulkActions, setShowBulkActions] = useState(false);
  const [selectedItems, setSelectedItems] = useState([]);
  const [showImportExport, setShowImportExport] = useState(false);
  const [showVersionHistory, setShowVersionHistory] = useState(false);

  // Editor state
  const [editorData, setEditorData] = useState({
    id: '',
    page_section: '',
    section_type: 'text',
    title: '',
    content: '',
    content_fr: '',
    metadata: {},
    is_published: true,
    display_order: 1
  });

  // Enhanced content structure with all website sections
  const contentStructure = {
    homepage: {
      label: 'Page d\'Accueil',
      sections: [
        { key: 'hero_section', label: 'Section Héro', type: 'hero' },
        { key: 'featured_properties', label: 'Propriétés en Vedette', type: 'properties' },
        { key: 'market_intelligence', label: 'Intelligence du Marché', type: 'analytics' },
        { key: 'testimonials', label: 'Témoignages', type: 'testimonials' },
        { key: 'legal_services', label: 'Services Juridiques', type: 'services' },
        { key: 'commune_explorer', label: 'Explorateur de Communes', type: 'navigation' },
        { key: 'footer', label: 'Pied de Page', type: 'footer' }
      ]
    },
    'property-search-listings': {
      label: 'Recherche de Propriétés',
      sections: [
        { key: 'search_header', label: 'En-tête de Recherche', type: 'search' },
        { key: 'search_filters', label: 'Filtres de Recherche', type: 'filters' },
        { key: 'property_list_view', label: 'Vue Liste des Propriétés', type: 'listing' },
        { key: 'property_map', label: 'Carte des Propriétés', type: 'map' },
        { key: 'property_card', label: 'Cartes de Propriétés', type: 'card' }
      ]
    },
    'individual-property': {
      label: 'Page Propriété Individuelle',
      sections: [
        { key: 'property_details', label: 'Détails de la Propriété', type: 'details' },
        { key: 'property_video_player', label: 'Lecteur Vidéo', type: 'video' },
        { key: 'scheduling_section', label: 'Section Planification', type: 'booking' },
        { key: 'inquiry_form', label: 'Formulaire de Demande', type: 'form' },
        { key: 'investment_analysis', label: 'Analyse d\'Investissement', type: 'analytics' },
        { key: 'social_proof_section', label: 'Preuves Sociales', type: 'testimonials' },
        { key: 'commune_context', label: 'Contexte de la Commune', type: 'location' },
        { key: 'related_properties', label: 'Propriétés Similaires', type: 'recommendations' }
      ]
    },
    'legal-services': {
      label: 'Services Juridiques',
      sections: [
        { key: 'service_overview', label: 'Aperçu des Services', type: 'overview' },
        { key: 'document_preparation', label: 'Préparation de Documents', type: 'services' },
        { key: 'verification_center', label: 'Centre de Vérification', type: 'verification' },
        { key: 'legal_education', label: 'Éducation Juridique', type: 'educational' },
        { key: 'trust_indicators', label: 'Indicateurs de Confiance', type: 'trust' },
        { key: 'appointment_scheduler', label: 'Planificateur de Rendez-vous', type: 'booking' },
        { key: 'faq_section', label: 'FAQ', type: 'faq' }
      ]
    },
    'about-trust': {
      label: 'À Propos & Confiance',
      sections: [
        { key: 'hero_section', label: 'Section Héro', type: 'hero' },
        { key: 'mission_section', label: 'Notre Mission', type: 'mission' },
        { key: 'team_section', label: 'Notre Équipe', type: 'team' },
        { key: 'trust_section', label: 'Indicateurs de Confiance', type: 'trust' },
        { key: 'testimonials_section', label: 'Témoignages', type: 'testimonials' },
        { key: 'partnerships_section', label: 'Partenariats', type: 'partnerships' },
        { key: 'contact_section', label: 'Contact', type: 'contact' }
      ]
    },
    'commune-explorer': {
      label: 'Explorateur de Communes',
      sections: [
        { key: 'commune_hero', label: 'Section Héro', type: 'hero' },
        { key: 'commune_selector', label: 'Sélecteur de Communes', type: 'selector' },
        { key: 'commune_overview', label: 'Aperçu de la Commune', type: 'overview' },
        { key: 'infrastructure_section', label: 'Infrastructure', type: 'infrastructure' },
        { key: 'lifestyle_section', label: 'Style de Vie', type: 'lifestyle' },
        { key: 'investment_insights', label: 'Perspectives d\'Investissement', type: 'analytics' },
        { key: 'property_showcase', label: 'Vitrine des Propriétés', type: 'properties' },
        { key: 'resident_reviews', label: 'Avis des Résidents', type: 'reviews' }
      ]
    },
    'admin-dashboard': {
      label: 'Tableau de Bord Admin',
      sections: [
        { key: 'dashboard_stats', label: 'Statistiques', type: 'stats' },
        { key: 'user_management', label: 'Gestion des Utilisateurs', type: 'management' },
        { key: 'property_management', label: 'Gestion des Propriétés', type: 'management' },
        { key: 'content_management', label: 'Gestion du Contenu', type: 'management' },
        { key: 'activity_monitor', label: 'Moniteur d\'Activité', type: 'monitoring' },
        { key: 'notification_center', label: 'Centre de Notifications', type: 'notifications' },
        { key: 'appointment_management', label: 'Gestion des Rendez-vous', type: 'management' }
      ]
    }
  };

  // Load content and statistics on mount
  useEffect(() => {
    loadContentAndStats();
  }, []);

  // Load all content and statistics
  const loadContentAndStats = async () => {
    setLoading(true);
    try {
      await Promise.all([loadContent(), loadContentStats()]);
    } catch (error) {
      console.error('Error loading content and stats:', error);
      setErrorMessage('Erreur lors du chargement des données');
    } finally {
      setLoading(false);
    }
  };

  // Load all content from database
  const loadContent = async () => {
    try {
      const content = await frontendContentService?.getAllContent();
      setContentItems(content || []);
      console.log('✅ Contenu universel chargé:', content?.length || 0, 'éléments');

      // Generate default content if empty
      if (!content || content?.length === 0) {
        console.log('🔄 Génération de la structure de contenu par défaut...');
        await generateDefaultContent();
      }

    } catch (error) {
      console.error('Error loading content:', error);
      setErrorMessage('Erreur lors du chargement du contenu');
    }
  };

  // Load content statistics
  const loadContentStats = async () => {
    try {
      const stats = await frontendContentService?.getContentStatistics();
      setContentStats(stats);
      console.log('✅ Statistiques de contenu chargées:', stats);
    } catch (error) {
      console.error('Error loading content statistics:', error);
    }
  };

  // Generate comprehensive default content structure
  const generateDefaultContent = async () => {
    try {
      const defaultContent = [];
      let orderIndex = 1;

      // Generate content for each page and section
      Object.entries(contentStructure)?.forEach(([pageKey, page]) => {
        page?.sections?.forEach((section) => {
          defaultContent?.push({
            page_section: `${pageKey}.${section?.key}`,
            section_type: section?.type,
            title: section?.label,
            content: getDefaultContent(section?.type, section?.key),
            content_fr: getDefaultContentFr(section?.type, section?.key),
            metadata: getDefaultMetadata(section?.type, pageKey, section?.key),
            is_published: true,
            display_order: orderIndex++
          });
        });
      });

      console.log('🔄 Création de', defaultContent?.length, 'sections de contenu par défaut...');

      // Batch create content
      const results = await frontendContentService?.createBulkContent(defaultContent);
      if (results?.length > 0) {
        setContentItems(results);
        setSuccessMessage(`Structure de contenu complète créée : ${results?.length} sections générées !`);
        setTimeout(() => setSuccessMessage(''), 5000);
      }

    } catch (error) {
      console.error('Error generating default content:', error);
      setErrorMessage('Erreur lors de la génération du contenu par défaut');
    }
  };

  // Enhanced default content generation
  const getDefaultContent = (type, key) => {
    const contentTemplates = {
      hero: key?.includes('commune') 
        ? 'Découvrez les communes d\'Abidjan avec des informations complètes sur chaque quartier'
        : 'Bienvenue sur la plateforme immobilière premium d\'Abidjan',
      text: 'Contenu textuel par défaut qui peut être personnalisé selon vos besoins',
      services: 'Nos services complets incluent la consultation juridique, la préparation de documents et l\'accompagnement personnalisé',
      testimonials: 'Ce que nos clients disent de nous - témoignages authentiques de satisfaction',
      team: 'Rencontrez notre équipe de professionnels expérimentés en immobilier et services juridiques',
      trust: 'Indicateurs de confiance : certifications, partenaires agréés et garanties',
      analytics: 'Analyses de marché et données d\'investissement pour des décisions éclairées',
      properties: 'Découvrez notre sélection de propriétés premium dans toutes les communes d\'Abidjan',
      educational: 'Guides éducatifs et conseils d\'experts pour réussir vos projets immobiliers',
      faq: 'Questions fréquemment posées avec des réponses détaillées',
      contact: 'Contactez-nous pour plus d\'informations et un accompagnement personnalisé',
      booking: 'Planifiez votre rendez-vous avec nos experts',
      search: 'Utilisez nos outils de recherche avancés pour trouver la propriété idéale',
      filters: 'Filtrez les propriétés par prix, type, localisation et caractéristiques',
      listing: 'Parcourez notre catalogue complet de propriétés',
      details: 'Informations détaillées sur cette propriété',
      management: 'Interface de gestion avancée avec contrôles administratifs'
    };
    
    return contentTemplates?.[type] || 'Contenu par défaut à personnaliser';
  };

  // Enhanced French content generation
  const getDefaultContentFr = (type, key) => {
    const frenchTemplates = {
      hero: key?.includes('commune') 
        ? 'Explorez les communes d\'Abidjan avec des informations détaillées sur chaque quartier'
        : 'Plateforme immobilière de référence à Abidjan - Trouvez votre propriété idéale',
      text: 'Texte français par défaut personnalisable selon vos exigences spécifiques',
      services: 'Services professionnels : conseil juridique, préparation documentaire, suivi personnalisé',
      testimonials: 'Témoignages clients - retours d\'expérience de nos clients satisfaits',
      team: 'Équipe d\'experts en immobilier et droit avec une expérience reconnue à Abidjan',
      trust: 'Garanties de confiance : certifications officielles, partenaires agréés, sécurité',
      analytics: 'Données du marché immobilier abidjanais pour des investissements réfléchis',
      properties: 'Collection exclusive de biens immobiliers dans toutes les communes d\'Abidjan',
      educational: 'Ressources éducatives et conseils d\'experts immobiliers',
      faq: 'Foire aux questions avec réponses complètes de nos spécialistes',
      contact: 'Contactez nos experts pour un accompagnement sur mesure',
      booking: 'Réservez votre consultation avec nos conseillers spécialisés',
      search: 'Recherche avancée pour identifier la propriété correspondant à vos critères',
      filters: 'Options de filtrage par prix, type de bien, quartier et équipements',
      listing: 'Catalogue exhaustif des biens immobiliers disponibles',
      details: 'Fiche détaillée de cette propriété avec toutes les informations',
      management: 'Interface de gestion complète avec outils d\'administration'
    };
    
    return frenchTemplates?.[type] || 'Contenu français par défaut à adapter';
  };

  // Enhanced metadata generation
  const getDefaultMetadata = (type, page, key) => {
    return {
      editable: true,
      version: '1.0',
      last_modified: new Date()?.toISOString(),
      content_type: type,
      page_context: page,
      section_key: key,
      seo_optimized: false,
      priority: type === 'hero' ? 'high' : 'normal',
      mobile_optimized: true,
      accessibility_checked: false,
      translation_status: 'auto_generated'
    };
  };

  // Handle content editing
  const handleEdit = (content) => {
    setSelectedContent(content);
    setEditorData({
      id: content?.id || '',
      page_section: content?.page_section || '',
      section_type: content?.section_type || 'text',
      title: content?.title || '',
      content: content?.content || '',
      content_fr: content?.content_fr || '',
      metadata: content?.metadata || {},
      is_published: content?.is_published ?? true,
      display_order: content?.display_order || 1
    });
    setShowEditor(true);
  };

  // Handle new content creation
  const handleCreate = () => {
    setSelectedContent(null);
    setEditorData({
      id: '',
      page_section: '',
      section_type: 'text',
      title: '',
      content: '',
      content_fr: '',
      metadata: { 
        editable: true, 
        version: '1.0',
        content_type: 'text',
        priority: 'normal',
        mobile_optimized: true
      },
      is_published: true,
      display_order: 1
    });
    setShowEditor(true);
  };

  // Enhanced save functionality
  const handleSave = async () => {
    if (!editorData?.title?.trim() || !editorData?.content?.trim()) {
      setErrorMessage('Le titre et le contenu sont obligatoires');
      return;
    }

    setSaving(true);
    setErrorMessage('');

    try {
      let result;

      if (editorData?.id) {
        // Update existing content
        result = await frontendContentService?.updateContent(editorData?.id, {
          page_section: editorData?.page_section,
          section_type: editorData?.section_type,
          title: editorData?.title?.trim(),
          content: editorData?.content?.trim(),
          content_fr: editorData?.content_fr?.trim() || editorData?.content?.trim(),
          metadata: {
            ...editorData?.metadata,
            last_modified: new Date()?.toISOString(),
            version: (parseFloat(editorData?.metadata?.version || '1.0') + 0.1)?.toFixed(1)
          },
          is_published: editorData?.is_published,
          display_order: parseInt(editorData?.display_order) || 1
        });

        console.log('✅ Contenu mis à jour:', result);

      } else {
        // Create new content
        result = await frontendContentService?.createContent({
          page_section: editorData?.page_section,
          section_type: editorData?.section_type,
          title: editorData?.title?.trim(),
          content: editorData?.content?.trim(),
          content_fr: editorData?.content_fr?.trim() || editorData?.content?.trim(),
          metadata: {
            ...editorData?.metadata,
            last_modified: new Date()?.toISOString(),
            version: '1.0'
          },
          is_published: editorData?.is_published,
          display_order: parseInt(editorData?.display_order) || 1
        });

        console.log('✅ Nouveau contenu créé:', result);
      }

      if (result) {
        setSuccessMessage(`Contenu "${result?.title}" sauvegardé avec succès !`);
        setShowEditor(false);
        await loadContentAndStats();
        setTimeout(() => setSuccessMessage(''), 5000);
      }

    } catch (error) {
      console.error('Error saving content:', error);
      setErrorMessage(`Erreur lors de la sauvegarde: ${error?.message || 'Erreur inconnue'}`);
    } finally {
      setSaving(false);
    }
  };

  // Enhanced delete with confirmation
  const handleDelete = async (contentId, title) => {
    if (!window.confirm(`⚠️ Êtes-vous sûr de vouloir supprimer "${title}" ?\n\nCette action est irréversible.`)) {
      return;
    }

    try {
      await frontendContentService?.deleteContent(contentId);
      console.log('✅ Contenu supprimé:', title);
      setSuccessMessage(`Contenu "${title}" supprimé avec succès !`);
      await loadContentAndStats();
      setTimeout(() => setSuccessMessage(''), 3000);

    } catch (error) {
      console.error('Error deleting content:', error);
      setErrorMessage(`Erreur lors de la suppression: ${error?.message}`);
    }
  };

  // Toggle publish status
  const handleTogglePublish = async (content) => {
    try {
      await frontendContentService?.togglePublishStatus(content?.id, !content?.is_published);
      console.log('✅ Statut de publication modifié:', content?.title);
      await loadContentAndStats();

    } catch (error) {
      console.error('Error toggling publish status:', error);
      setErrorMessage('Erreur lors de la modification du statut');
    }
  };

  // Bulk operations
  const handleBulkPublish = async (publish = true) => {
    if (selectedItems?.length === 0) return;

    const action = publish ? 'publier' : 'dépublier';
    if (!window.confirm(`Voulez-vous ${action} ${selectedItems?.length} élément(s) sélectionné(s) ?`)) {
      return;
    }

    try {
      await frontendContentService?.bulkUpdateStatus(selectedItems, publish);
      setSuccessMessage(`${selectedItems?.length} élément(s) ${publish ? 'publié(s)' : 'dépublié(s)'} avec succès !`);
      setSelectedItems([]);
      await loadContentAndStats();
      setTimeout(() => setSuccessMessage(''), 3000);

    } catch (error) {
      console.error('Error in bulk operation:', error);
      setErrorMessage(`Erreur lors de l'opération groupée: ${error?.message}`);
    }
  };

  // Duplicate content
  const handleDuplicate = async (content) => {
    try {
      const duplicatedContent = {
        ...content,
        title: `${content?.title} (Copie)`,
        page_section: `${content?.page_section}_copy`,
        is_published: false,
        metadata: {
          ...content?.metadata,
          version: '1.0',
          last_modified: new Date()?.toISOString(),
          duplicated_from: content?.id
        }
      };

      delete duplicatedContent?.id;
      delete duplicatedContent?.created_at;
      delete duplicatedContent?.updated_at;

      let result = await frontendContentService?.createContent(duplicatedContent);
      if (result) {
        setSuccessMessage(`Contenu "${content?.title}" dupliqué avec succès !`);
        await loadContentAndStats();
        setTimeout(() => setSuccessMessage(''), 3000);
      }

    } catch (error) {
      console.error('Error duplicating content:', error);
      setErrorMessage(`Erreur lors de la duplication: ${error?.message}`);
    }
  };

  // Advanced filtering
  const filteredContent = contentItems?.filter(item => {
    const matchesSearch = !searchTerm || 
      item?.title?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
      item?.content?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
      item?.page_section?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
      item?.section_type?.toLowerCase()?.includes(searchTerm?.toLowerCase());

    const matchesPage = filterPage === 'all' || item?.page_section?.startsWith(filterPage);
    const matchesType = filterType === 'all' || item?.section_type === filterType;
    const matchesStatus = filterStatus === 'all' || 
      (filterStatus === 'published' && item?.is_published) ||
      (filterStatus === 'draft' && !item?.is_published);

    return matchesSearch && matchesPage && matchesType && matchesStatus;
  });

  // Get unique values for filters
  const uniquePages = [...new Set(contentItems?.map(item => item?.page_section?.split('.')?.[0]))]?.filter(Boolean);
  const uniqueTypes = [...new Set(contentItems?.map(item => item?.section_type))]?.filter(Boolean);

  return (
    <>
      <Helmet>
        <title>Système Universel de Gestion de Contenu - KAPITALSTATES</title>
        <meta name="description" content="Interface administrative complète pour gérer tout le contenu frontend de KAPITALSTATES sans exception" />
      </Helmet>
      <div className="min-h-screen bg-gray-50 p-4">
        <div className="max-w-7xl mx-auto space-y-6">
          {/* Header */}
          <div className="bg-white rounded-lg shadow-sm p-6 border border-gray-200">
            <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
              <div>
                <h1 className="text-3xl font-bold text-gray-900 mb-2 flex items-center gap-3">
                  <Database className="h-8 w-8 text-blue-600" />
                  Système Universel de Gestion de Contenu
                </h1>
                <p className="text-gray-600">
                  Contrôlez intégralement tout le contenu de votre plateforme KAPITALSTATES avec des outils d'édition avancés
                </p>
              </div>
              
              <div className="flex flex-wrap gap-3">
                <Button
                  onClick={() => loadContentAndStats()}
                  variant="outline"
                  disabled={loading}
                  className="flex items-center gap-2"
                >
                  <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
                  Actualiser
                </Button>

                <Button
                  onClick={() => setShowBulkActions(!showBulkActions)}
                  variant="outline"
                  className="flex items-center gap-2"
                >
                  <Settings className="h-4 w-4" />
                  Actions Groupées
                </Button>
                
                <Button
                  onClick={() => handleCreate()}
                  className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700"
                >
                  <Plus className="h-4 w-4" />
                  Nouveau Contenu
                </Button>
              </div>
            </div>

            {/* Success/Error Messages */}
            {successMessage && (
              <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded-lg flex items-center gap-2">
                <CheckCircle className="h-5 w-5 text-green-600" />
                <span className="text-green-800">{successMessage}</span>
              </div>
            )}

            {errorMessage && (
              <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2">
                <AlertCircle className="h-5 w-5 text-red-600" />
                <span className="text-red-800">{errorMessage}</span>
              </div>
            )}
          </div>

          {/* Enhanced Statistics Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">Total Éléments</p>
                  <p className="text-2xl font-bold text-gray-900">{contentStats?.total_content || contentItems?.length || 0}</p>
                </div>
                <div className="p-3 bg-blue-50 rounded-full">
                  <FileText className="w-6 h-6 text-blue-600" />
                </div>
              </div>
            </div>

            <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">Contenu Publié</p>
                  <p className="text-2xl font-bold text-green-600">
                    {contentStats?.published_content || contentItems?.filter(item => item?.is_published)?.length || 0}
                  </p>
                </div>
                <div className="p-3 bg-green-50 rounded-full">
                  <Globe className="w-6 h-6 text-green-600" />
                </div>
              </div>
            </div>

            <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">Brouillons</p>
                  <p className="text-2xl font-bold text-yellow-600">
                    {contentStats?.draft_content || contentItems?.filter(item => !item?.is_published)?.length || 0}
                  </p>
                </div>
                <div className="p-3 bg-yellow-50 rounded-full">
                  <Edit3 className="w-6 h-6 text-yellow-600" />
                </div>
              </div>
            </div>

            <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">Pages Gérées</p>
                  <p className="text-2xl font-bold text-purple-600">
                    {contentStats?.unique_pages || uniquePages?.length || 0}
                  </p>
                </div>
                <div className="p-3 bg-purple-50 rounded-full">
                  <Settings className="w-6 h-6 text-purple-600" />
                </div>
              </div>
            </div>
          </div>

          {/* Advanced Search and Filters */}
          <div className="bg-white rounded-lg shadow-sm p-4 border border-gray-200">
            <div className="flex flex-col lg:flex-row gap-4 items-center">
              <div className="flex-1">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <Input
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e?.target?.value)}
                    placeholder="Rechercher par titre, contenu, section ou type..."
                    className="pl-10"
                  />
                </div>
              </div>
              
              <div className="flex flex-wrap gap-3">
                <Select
                  value={filterPage}
                  onChange={(e) => setFilterPage(e?.target?.value)}
                  className="w-48"
                >
                  <option value="all">Toutes les pages</option>
                  {uniquePages?.map(page => (
                    <option key={page} value={page}>
                      {contentStructure?.[page]?.label || page}
                    </option>
                  ))}
                </Select>

                <Select
                  value={filterType}
                  onChange={(e) => setFilterType(e?.target?.value)}
                  className="w-40"
                >
                  <option value="all">Tous types</option>
                  {uniqueTypes?.map(type => (
                    <option key={type} value={type}>
                      {type}
                    </option>
                  ))}
                </Select>

                <Select
                  value={filterStatus}
                  onChange={(e) => setFilterStatus(e?.target?.value)}
                  className="w-36"
                >
                  <option value="all">Tous statuts</option>
                  <option value="published">Publié</option>
                  <option value="draft">Brouillon</option>
                </Select>

                <Button
                  onClick={() => {
                    setSearchTerm('');
                    setFilterPage('all');
                    setFilterType('all');
                    setFilterStatus('all');
                  }}
                  variant="outline"
                  className="text-gray-500"
                >
                  Réinitialiser
                </Button>
              </div>
            </div>
          </div>

          {/* Bulk Actions Panel */}
          {showBulkActions && (
            <div className="bg-white rounded-lg shadow-sm p-4 border border-gray-200">
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div>
                  <h3 className="text-lg font-medium text-gray-900 mb-1">Actions Groupées</h3>
                  <p className="text-sm text-gray-600">
                    {selectedItems?.length > 0 
                      ? `${selectedItems?.length} élément(s) sélectionné(s)`
                      : 'Sélectionnez des éléments pour effectuer des actions groupées'
                    }
                  </p>
                </div>

                {selectedItems?.length > 0 && (
                  <div className="flex gap-2">
                    <Button
                      onClick={() => handleBulkPublish(true)}
                      className="bg-green-600 hover:bg-green-700 text-white"
                    >
                      Publier
                    </Button>
                    <Button
                      onClick={() => handleBulkPublish(false)}
                      className="bg-yellow-600 hover:bg-yellow-700 text-white"
                    >
                      Dépublier
                    </Button>
                    <Button
                      onClick={() => setSelectedItems([])}
                      variant="outline"
                    >
                      Désélectionner
                    </Button>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Universal Content Management Interface */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-medium text-gray-900">
                  Contenu KAPITALSTATES ({filteredContent?.length || 0})
                </h3>
                
                {showBulkActions && (
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      onChange={(e) => {
                        if (e?.target?.checked) {
                          setSelectedItems(filteredContent?.map(item => item?.id) || []);
                        } else {
                          setSelectedItems([]);
                        }
                      }}
                      checked={selectedItems?.length === filteredContent?.length && filteredContent?.length > 0}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <span className="text-sm text-gray-600">Tout sélectionner</span>
                  </div>
                )}
              </div>
            </div>
            
            {loading ? (
              <div className="p-8 text-center">
                <RefreshCw className="h-8 w-8 animate-spin mx-auto text-blue-600 mb-4" />
                <p className="text-gray-500">Chargement du contenu universel...</p>
              </div>
            ) : filteredContent?.length > 0 ? (
              <div className="divide-y divide-gray-200">
                {filteredContent?.map(item => (
                  <div key={item?.id} className="p-4 hover:bg-gray-50 transition-colors">
                    <div className="flex items-center gap-4">
                      {showBulkActions && (
                        <input
                          type="checkbox"
                          checked={selectedItems?.includes(item?.id)}
                          onChange={(e) => {
                            if (e?.target?.checked) {
                              setSelectedItems(prev => [...prev, item?.id]);
                            } else {
                              setSelectedItems(prev => prev?.filter(id => id !== item?.id));
                            }
                          }}
                          className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                      )}

                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <div className="flex items-center gap-2">
                            {/* Dynamic icons based on content type */}
                            {item?.section_type === 'hero' && <Globe className="h-4 w-4 text-blue-600" />}
                            {item?.section_type === 'services' && <Settings className="h-4 w-4 text-green-600" />}
                            {item?.section_type === 'testimonials' && <Users className="h-4 w-4 text-yellow-600" />}
                            {item?.section_type === 'management' && <Database className="h-4 w-4 text-purple-600" />}
                            {(!['hero', 'services', 'testimonials', 'management']?.includes(item?.section_type)) && (
                              <FileText className="h-4 w-4 text-gray-600" />
                            )}
                            
                            <h4 className="font-medium text-gray-900">{item?.title}</h4>
                          </div>

                          <div className="flex items-center gap-2">
                            <span className="text-xs px-2 py-1 bg-gray-100 text-gray-600 rounded-full">
                              {item?.section_type}
                            </span>
                            
                            <span className={`text-xs px-2 py-1 rounded-full ${
                              item?.is_published 
                                ? 'bg-green-100 text-green-800' :'bg-yellow-100 text-yellow-800'
                            }`}>
                              {item?.is_published ? 'Publié' : 'Brouillon'}
                            </span>

                            {item?.metadata?.priority === 'high' && (
                              <span className="text-xs px-2 py-1 bg-red-100 text-red-800 rounded-full">
                                Priorité Élevée
                              </span>
                            )}
                          </div>
                        </div>
                        
                        <div className="text-sm text-gray-600 mb-2">
                          <span className="font-medium">Section:</span> {item?.page_section}
                          {contentStructure?.[item?.page_section?.split('.')?.[0]] && (
                            <span className="ml-2 text-gray-500">
                              ({contentStructure?.[item?.page_section?.split('.')?.[0]]?.label})
                            </span>
                          )}
                        </div>
                        
                        <div className="text-sm text-gray-500 line-clamp-2 mb-2">
                          {item?.content?.substring(0, 200)}
                          {item?.content?.length > 200 ? '...' : ''}
                        </div>
                        
                        <div className="flex items-center gap-4 text-xs text-gray-500">
                          <span>Ordre: {item?.display_order}</span>
                          <span>Version: {item?.metadata?.version || '1.0'}</span>
                          {item?.metadata?.last_modified && (
                            <span>
                              Modifié: {new Date(item?.metadata?.last_modified)?.toLocaleDateString('fr-FR')}
                            </span>
                          )}
                          {item?.content_fr && (
                            <span className="text-blue-600">Bilingue</span>
                          )}
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-2">
                        <Button
                          onClick={() => handleDuplicate(item)}
                          variant="outline"
                          size="sm"
                          className="text-purple-600 hover:text-purple-700"
                          title="Dupliquer"
                        >
                          <Copy className="h-4 w-4" />
                        </Button>

                        <Button
                          onClick={() => handleEdit(item)}
                          variant="outline"
                          size="sm"
                          className="text-blue-600 hover:text-blue-700"
                          title="Modifier"
                        >
                          <Edit3 className="h-4 w-4" />
                        </Button>
                        
                        <Button
                          onClick={() => handleTogglePublish(item)}
                          variant="outline"
                          size="sm"
                          className={item?.is_published ? 'text-yellow-600 hover:text-yellow-700' : 'text-green-600 hover:text-green-700'}
                          title={item?.is_published ? 'Dépublier' : 'Publier'}
                        >
                          {item?.is_published ? <Eye className="h-4 w-4" /> : <Globe className="h-4 w-4" />}
                        </Button>
                        
                        <Button
                          onClick={() => handleDelete(item?.id, item?.title)}
                          variant="outline"
                          size="sm"
                          className="text-red-600 hover:text-red-700"
                          title="Supprimer"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="p-8 text-center">
                <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Database className="h-8 w-8 text-gray-400" />
                </div>
                <p className="text-gray-500 mb-4">
                  {searchTerm || filterPage !== 'all' || filterType !== 'all' || filterStatus !== 'all' ?'Aucun contenu ne correspond aux critères de recherche' :'Aucun contenu disponible'
                  }
                </p>
                {!searchTerm && filterPage === 'all' && filterType === 'all' && filterStatus === 'all' && (
                  <Button
                    onClick={() => handleCreate()}
                    className="bg-blue-600 hover:bg-blue-700"
                  >
                    Créer votre premier contenu
                  </Button>
                )}
              </div>
            )}
          </div>

          {/* Universal Content Editor Modal */}
          {showEditor && (
            <div className="fixed inset-0 z-50 overflow-y-auto bg-black bg-opacity-50">
              <div className="flex min-h-screen items-center justify-center p-4">
                <div className="relative w-full max-w-6xl bg-white rounded-lg shadow-xl max-h-[95vh] overflow-y-auto">
                  {/* Editor Header */}
                  <div className="sticky top-0 bg-white border-b border-gray-200 p-6 rounded-t-lg z-10">
                    <div className="flex items-center justify-between">
                      <div>
                        <h2 className="text-xl font-semibold text-gray-900">
                          {selectedContent ? 'Modifier le Contenu Universel' : 'Nouveau Contenu Universel'}
                        </h2>
                        <p className="text-sm text-gray-500">
                          {selectedContent 
                            ? `Modifiez "${selectedContent?.title}" - Section: ${selectedContent?.page_section}`
                            : 'Créez un nouveau contenu pour votre plateforme KAPITALSTATES'
                          }
                        </p>
                      </div>
                      <Button
                        onClick={() => setShowEditor(false)}
                        variant="outline"
                        className="text-gray-500 hover:text-gray-700"
                      >
                        ✕
                      </Button>
                    </div>
                  </div>

                  {/* Enhanced Editor Form */}
                  <div className="p-6 space-y-8">
                    {/* Basic Information */}
                    <div className="space-y-4">
                      <h3 className="text-lg font-medium text-gray-900 border-b border-gray-200 pb-2 flex items-center gap-2">
                        <Settings className="h-5 w-5" />
                        Informations de Base
                      </h3>
                      
                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Titre du Contenu *
                          </label>
                          <Input
                            value={editorData?.title}
                            onChange={(e) => setEditorData(prev => ({ ...prev, title: e?.target?.value }))}
                            placeholder="Titre descriptif du contenu"
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Section de Page *
                          </label>
                          <Input
                            value={editorData?.page_section}
                            onChange={(e) => setEditorData(prev => ({ ...prev, page_section: e?.target?.value }))}
                            placeholder="ex: homepage.hero_section"
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Type de Contenu
                          </label>
                          <Select
                            value={editorData?.section_type}
                            onChange={(e) => setEditorData(prev => ({ ...prev, section_type: e?.target?.value }))}
                          >
                            <option value="text">Texte</option>
                            <option value="hero">Section Héro</option>
                            <option value="services">Services</option>
                            <option value="testimonials">Témoignages</option>
                            <option value="team">Équipe</option>
                            <option value="trust">Confiance</option>
                            <option value="analytics">Analytics</option>
                            <option value="properties">Propriétés</option>
                            <option value="educational">Éducatif</option>
                            <option value="faq">FAQ</option>
                            <option value="contact">Contact</option>
                            <option value="booking">Réservation</option>
                            <option value="search">Recherche</option>
                            <option value="filters">Filtres</option>
                            <option value="management">Gestion</option>
                            <option value="overview">Aperçu</option>
                            <option value="details">Détails</option>
                          </Select>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Ordre d'Affichage
                          </label>
                          <Input
                            type="number"
                            value={editorData?.display_order}
                            onChange={(e) => setEditorData(prev => ({ ...prev, display_order: e?.target?.value }))}
                            min="1"
                          />
                        </div>
                      </div>
                    </div>

                    {/* Content Section */}
                    <div className="space-y-4">
                      <h3 className="text-lg font-medium text-gray-900 border-b border-gray-200 pb-2 flex items-center gap-2">
                        <FileText className="h-5 w-5" />
                        Contenu Multilingue
                      </h3>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Contenu Principal (Anglais) *
                        </label>
                        <textarea
                          value={editorData?.content}
                          onChange={(e) => setEditorData(prev => ({ ...prev, content: e?.target?.value }))}
                          rows={8}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-vertical"
                          placeholder="Saisissez le contenu principal en anglais..."
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Contenu Français
                        </label>
                        <textarea
                          value={editorData?.content_fr}
                          onChange={(e) => setEditorData(prev => ({ ...prev, content_fr: e?.target?.value }))}
                          rows={8}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-vertical"
                          placeholder="Traduction française du contenu..."
                        />
                      </div>
                    </div>

                    {/* Advanced Settings */}
                    <div className="space-y-4">
                      <h3 className="text-lg font-medium text-gray-900 border-b border-gray-200 pb-2 flex items-center gap-2">
                        <Settings className="h-5 w-5" />
                        Paramètres Avancés
                      </h3>
                      
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div className="space-y-4">
                          <h4 className="font-medium text-gray-900">État de Publication</h4>
                          <div className="flex items-center gap-6">
                            <label className="flex items-center">
                              <input
                                type="checkbox"
                                checked={editorData?.is_published}
                                onChange={(e) => setEditorData(prev => ({ ...prev, is_published: e?.target?.checked }))}
                                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 mr-2"
                              />
                              <span className="text-sm text-gray-700">Publié</span>
                            </label>
                          </div>
                        </div>

                        <div className="space-y-4">
                          <h4 className="font-medium text-gray-900">Priorité du Contenu</h4>
                          <Select
                            value={editorData?.metadata?.priority || 'normal'}
                            onChange={(e) => setEditorData(prev => ({ 
                              ...prev, 
                              metadata: { ...prev?.metadata, priority: e?.target?.value }
                            }))}
                          >
                            <option value="low">Basse</option>
                            <option value="normal">Normale</option>
                            <option value="high">Élevée</option>
                            <option value="critical">Critique</option>
                          </Select>
                        </div>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <label className="flex items-center">
                          <input
                            type="checkbox"
                            checked={editorData?.metadata?.mobile_optimized ?? true}
                            onChange={(e) => setEditorData(prev => ({ 
                              ...prev, 
                              metadata: { ...prev?.metadata, mobile_optimized: e?.target?.checked }
                            }))}
                            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 mr-2"
                          />
                          <span className="text-sm text-gray-700">Optimisé Mobile</span>
                        </label>

                        <label className="flex items-center">
                          <input
                            type="checkbox"
                            checked={editorData?.metadata?.seo_optimized ?? false}
                            onChange={(e) => setEditorData(prev => ({ 
                              ...prev, 
                              metadata: { ...prev?.metadata, seo_optimized: e?.target?.checked }
                            }))}
                            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 mr-2"
                          />
                          <span className="text-sm text-gray-700">SEO Optimisé</span>
                        </label>

                        <label className="flex items-center">
                          <input
                            type="checkbox"
                            checked={editorData?.metadata?.accessibility_checked ?? false}
                            onChange={(e) => setEditorData(prev => ({ 
                              ...prev, 
                              metadata: { ...prev?.metadata, accessibility_checked: e?.target?.checked }
                            }))}
                            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 mr-2"
                          />
                          <span className="text-sm text-gray-700">Accessibilité Vérifiée</span>
                        </label>
                      </div>
                    </div>

                    {/* Form Actions */}
                    <div className="sticky bottom-0 bg-white border-t border-gray-200 p-4 -m-6 mt-6 rounded-b-lg">
                      <div className="flex justify-end gap-3">
                        <Button
                          onClick={() => setShowEditor(false)}
                          variant="outline"
                          disabled={saving}
                        >
                          Annuler
                        </Button>
                        
                        <Button
                          onClick={handleSave}
                          disabled={saving || !editorData?.title?.trim() || !editorData?.content?.trim()}
                          className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700"
                        >
                          {saving ? (
                            <RefreshCw className="h-4 w-4 animate-spin" />
                          ) : (
                            <Save className="h-4 w-4" />
                          )}
                          {saving ? 'Sauvegarde...' : 'Sauvegarder le Contenu'}
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default UniversalContentManagementSystem;